import { forwardRef, useImperativeHandle, useRef, useState, useCallback } from "react";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import {
  // The icon names used in MANUAL_CONTENT diagrams. Add new ones here when
  // you reference a new icon in manual_content.py.
  UserPlus, User, Buildings, HardDrives, PaperPlaneTilt,
  MagnifyingGlass, FileText, Wrench, ArrowsLeftRight,
  FilePlus, Sparkle, ShieldCheck, CheckCircle, Envelope,
  Camera, VideoCamera, Receipt, ChatCircleDots,
  Plus, Link, Eye, CreditCard,
  CaretRight, DownloadSimple,
} from "@phosphor-icons/react";
import { useT } from "@/lib/i18n";

const ICONS = {
  UserPlus, User, Buildings, HardDrives, PaperPlaneTilt,
  MagnifyingGlass, FileText, Wrench, ArrowsLeftRight,
  FilePlus, Sparkle, ShieldCheck, CheckCircle, Envelope,
  Camera, VideoCamera, Receipt, ChatCircleDots,
  Plus, Link, Eye, CreditCard,
};

/**
 * Visual workflow flowchart for /help sections.
 *
 *   <WorkflowDiagram steps={section.diagram} sectionTitle={section.title} />
 *
 * Each step shape: ``{icon: "Sparkle", label: "AI part match"}`` — the
 * ``icon`` string must match a Phosphor icon name from the ICONS map above.
 *
 * iter136.17 — the internal "Download PNG" button was removed in favour
 * of a unified action bar managed by the parent (HelpPage groups
 * Download PNG + Print as poster + Email side-by-side). The download
 * routine is exposed via ``forwardRef`` + ``useImperativeHandle`` so the
 * parent can wire its own button to ``ref.current.downloadPng()``.
 */
const WorkflowDiagram = forwardRef(function WorkflowDiagram(
  { steps, testIdPrefix = "wf-step", sectionTitle = "" },
  ref,
) {
  const t = useT();
  const captureRef = useRef(null);
  const [exporting, setExporting] = useState(false);

  const slugify = (s) =>
    (s || "workflow")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 60);

  const downloadPng = useCallback(async () => {
    const node = captureRef.current;
    if (!node) return;
    setExporting(true);
    try {
      // Render at 2× pixel density for crisp output in slides.
      const dataUrl = await toPng(node, {
        pixelRatio: 2,
        backgroundColor: "#ffffff",
        cacheBust: true,
        // Prevent button from appearing in the export
        filter: (n) => !(n?.dataset?.exportExclude === "true"),
      });
      const fileName = `workflow-${slugify(sectionTitle)}.png`;
      const a = document.createElement("a");
      a.download = fileName;
      a.href = dataUrl;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success(
        t("diagram.downloaded", "Saved {file}").replace("{file}", fileName)
      );
    } catch (err) {
      toast.error(t("diagram.download_failed", "Download failed"));
    } finally {
      setExporting(false);
    }
  }, [sectionTitle, t]);

  // iter136.17 — expose the download routine to the parent so the
  // unified action bar (rendered by HelpPage next to "Print as poster"
  // / "Email this section") can call it.
  useImperativeHandle(ref, () => ({
    downloadPng,
    isExporting: () => exporting,
  }), [downloadPng, exporting]);

  if (!Array.isArray(steps) || steps.length === 0) return null;
  return (
    <div className="mt-4 mb-1" data-testid="workflow-diagram-wrap">
      <div ref={captureRef} className="bg-white p-3 rounded-md">
        <ol
          className="flex flex-col md:flex-row md:flex-wrap md:items-stretch gap-2 md:gap-1"
          data-testid="workflow-diagram"
          aria-label={`Workflow with ${steps.length} steps`}
        >
          {steps.map((step, idx) => {
            const Icon = ICONS[step.icon] || Sparkle;
            const isLast = idx === steps.length - 1;
            return (
              <li
                key={`${step.icon}-${idx}`}
                className="flex md:flex-1 items-stretch gap-1.5"
                data-testid={`${testIdPrefix}-${idx}`}
              >
                <div className="flex-1 min-w-0 bg-gradient-to-br from-blue-50 to-violet-50 border border-blue-200 rounded-md px-3 py-2.5 flex items-start gap-2 shadow-sm">
                  <span className="w-5 h-5 rounded-full bg-blue-600 text-white text-[10px] font-black flex items-center justify-center shrink-0 mt-0.5"
                    aria-label={`Step ${idx + 1}`}>
                    {idx + 1}
                  </span>
                  <Icon size={18} weight="duotone" className="text-blue-700 shrink-0 mt-0.5" />
                  <span className="text-xs sm:text-sm font-semibold text-slate-800 leading-snug min-w-0 break-words">
                    {step.label}
                  </span>
                </div>
                {!isLast && (
                  <CaretRight
                    size={14} weight="bold"
                    className="hidden md:flex self-center text-slate-300 shrink-0"
                    aria-hidden="true"
                  />
                )}
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
});

export default WorkflowDiagram;
